/**
 * Secure Error Handler
 * Provides sanitized error handling for production UI to prevent information leakage
 */

import { getLogger } from '@artify/logger';

const logger = getLogger('dashboard:error-handler');

export interface ErrorInfo {
  code: string;
  message: string;
  userMessage: string;
  timestamp: number;
}

export interface ErrorWithCode extends Error {
  code?: string;
}

export type HandleableError = Error | ErrorWithCode | { message: string; code?: string; name?: string };

const SENSITIVE_PATTERNS = [/secret/i, /token/i, /password/i, /key/i, /authorization/i, /cookie/i, /bearer/i, /auth/i];

/**
 * Sanitizes error messages to remove potentially sensitive information
 */
export function sanitizeErrorMessage(error: HandleableError): string {
  if (!error) return 'Unknown error occurred';

  const message = error.message || String(error);

  // Check for sensitive patterns and redact them
  let sanitized = message;
  SENSITIVE_PATTERNS.forEach((pattern) => {
    sanitized = sanitized.replace(pattern, '[REDACTED]');
  });

  // Remove stack traces from user-facing messages
  sanitized = sanitized.split('\n')[0];

  return sanitized;
}

/**
 * Maps technical errors to user-friendly messages
 */
export function getUserFriendlyMessage(error: HandleableError): string {
  const errorCode = ('code' in error && error.code) || error.name || 'UNKNOWN_ERROR';

  switch (errorCode) {
    case 'NETWORK_ERROR':
    case 'TIMEOUT_ERROR':
      return 'Unable to connect to the server. Please check your internet connection.';

    case 'AUTH_ERROR':
    case 'UNAUTHORIZED':
      return 'Authentication failed. Please sign in again.';

    case 'FORBIDDEN':
      return "You don't have permission to perform this action.";

    case 'NOT_FOUND':
      return 'The requested resource was not found.';

    case 'VALIDATION_ERROR':
      return 'Invalid input provided. Please check your data.';

    case 'RATE_LIMIT_ERROR':
      return 'Too many requests. Please wait a moment and try again.';

    case 'SERVER_ERROR':
      return 'A server error occurred. Please try again later.';

    default:
      return 'An unexpected error occurred. Please try again.';
  }
}

/**
 * Securely logs errors for debugging without exposing sensitive information
 */
export function logSecureError(error: HandleableError, context?: Record<string, any>) {
  const sanitizedContext = context ? sanitizeContext(context) : {};
  const errorCode = ('code' in error && error.code) || error.name || 'UNKNOWN_ERROR';
  const errorMessage = sanitizeErrorMessage(error);

  // Convert to Error object if needed for logger interface
  const errorObj = error instanceof Error ? error : new Error(errorMessage);

  logger.error(`Error occurred: ${errorCode}`, errorObj, sanitizedContext);
}

/**
 * Sanitizes context object to remove sensitive information
 */
function sanitizeContext(context: Record<string, any>): Record<string, any> {
  const sanitized: Record<string, any> = {};

  Object.entries(context).forEach(([key, value]) => {
    if (SENSITIVE_PATTERNS.some((pattern) => pattern.test(key))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'string' && value.length > 100) {
      // Truncate very long strings that might contain sensitive data
      sanitized[key] = value.substring(0, 100) + '...';
    } else {
      sanitized[key] = value;
    }
  });

  return sanitized;
}

/**
 * Creates a standardized error info object
 */
export function createErrorInfo(error: HandleableError, context?: Record<string, any>): ErrorInfo {
  const code = ('code' in error && error.code) || error.name || 'UNKNOWN_ERROR';
  const message = sanitizeErrorMessage(error);
  const userMessage = getUserFriendlyMessage(error);

  // Log for debugging
  logSecureError(error, context);

  return {
    code,
    message,
    userMessage,
    timestamp: Date.now(),
  };
}
